import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Prospect, Proposal, SectionConfig } from '../../shared/types';
import { SectionRenderer } from '../../renderer/SectionRenderer';
import { AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

import { mockProspects } from '../../shared/data/prospects';

export const ProposalPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [loading, setLoading] = useState(true);

  // Dynamically generate sections based on prospect segment
  const getSections = (prospectSlug: string): SectionConfig[] => {
    const isFashion = prospectSlug === 'turquesa-fitness' || prospectSlug === 'santoriny-shoes' || prospectSlug === 'doha-shoes';
    const isTravel = prospectSlug === 'embarque-22' || prospectSlug === 'multimarcas-viagens';
    const isAppFirst = prospectSlug === 'mundo-apolo';
    
    const baseSections: SectionConfig[] = [
      { id: 'sec_1', type: 'hero', sort_order: 1, config: {}, is_visible: true },
      { id: 'sec_2', type: 'diagnosis', sort_order: 2, config: {}, is_visible: true },
      { id: 'sec_3', type: 'ecosystem', sort_order: 3, config: {}, is_visible: true },
    ];

    if (isFashion) {
      baseSections.push({ id: 'sec_ecom', type: 'ecommerce_teaser', sort_order: 4, config: {}, is_visible: true });
    }
    if (isTravel) {
      baseSections.push({ id: 'sec_portal', type: 'portal_teaser', sort_order: 4, config: {}, is_visible: true });
      baseSections.push({ id: 'sec_crm', type: 'crm_teaser', sort_order: 5, config: {}, is_visible: true });
    }
    if (isAppFirst) {
      baseSections.push({ id: 'sec_app', type: 'app_teaser', sort_order: 4, config: {}, is_visible: true });
    }

    baseSections.push(
      { id: 'sec_4', type: 'google_channel', sort_order: 6, config: {}, is_visible: true },
      { id: 'sec_5', type: 'roi_calculator', sort_order: 7, config: {}, is_visible: true },
      { id: 'sec_6', type: 'pricing', sort_order: 8, config: {}, is_visible: true },
      { id: 'sec_7', type: 'timeline', sort_order: 9, config: {}, is_visible: true },
      { id: 'sec_8', type: 'cta', sort_order: 10, config: {}, is_visible: true }
    );

    return baseSections;
  };

  useEffect(() => {
    // Inject noindex, nofollow for proposal privacy compliance (Seção 3)
    let metaNoIndex = document.querySelector("meta[name='robots']");
    if (!metaNoIndex) {
      metaNoIndex = document.createElement('meta');
      metaNoIndex.setAttribute('name', 'robots');
      document.head.appendChild(metaNoIndex);
    }
    metaNoIndex.setAttribute('content', 'noindex, nofollow');

    if (slug && mockProspects[slug]) {
      setProspect(mockProspects[slug]);
    } else if (slug) {
      setProspect({
        id: `prsp_${slug}`,
        name: `${slug.charAt(0).toUpperCase() + slug.slice(1)} Empreendimentos`,
        trade_name: slug.replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
        slug: slug,
        segment: 'Serviços Especializados',
        city: 'São Miguel do Oeste',
        state: 'SC',
        whatsapp: '554988416296',
        status: 'ACTIVE',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    }
    setLoading(false);
  }, [slug]);

  useEffect(() => {
    if (prospect?.theme) {
      const root = document.documentElement;
      root.style.setProperty('--ym-prospect-primary', prospect.theme.primary_color);
      if (prospect.theme.bg_color) root.style.setProperty('--ym-prospect-bg', prospect.theme.bg_color);
      if (prospect.theme.text_color) root.style.setProperty('--ym-prospect-text', prospect.theme.text_color);
      if (prospect.theme.surface_color) root.style.setProperty('--ym-prospect-surface', prospect.theme.surface_color);
      if (prospect.theme.accent_color) root.style.setProperty('--ym-prospect-accent', prospect.theme.accent_color);
    }
    return () => {
      // Optional: Cleanup if needed, but since it's a SPA it might be fine to leave it until the next prospect loads.
      const root = document.documentElement;
      root.style.removeProperty('--ym-prospect-primary');
      root.style.removeProperty('--ym-prospect-bg');
      root.style.removeProperty('--ym-prospect-text');
      root.style.removeProperty('--ym-prospect-surface');
      root.style.removeProperty('--ym-prospect-accent');
    }
  }, [prospect]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-slate-300 border-t-[var(--ym-prospect-primary)] rounded-full" />
      </div>
    );
  }

  if (!prospect) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex flex-col items-center justify-center p-4 text-center">
        <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
        <h1 className="text-2xl font-bold mb-2">Proposta Não Encontrada</h1>
        <p className="text-slate-500 text-sm max-w-md mb-6">
          Não localizamos nenhuma proposta ativa para o endereço <code className="text-slate-700 bg-slate-100 px-1 rounded">/{slug}</code>.
        </p>
        <Link to="/admin" className="text-sm font-semibold text-blue-600 underline">
          Voltar para Home
        </Link>
      </div>
    );
  }

  const getDynamicProposal = (): Proposal => ({
    id: `prp_${prospect.slug}_01`,
    prospect_id: prospect.id,
    title: `Transformação Digital & Ecossistema de Vendas ${prospect.trade_name}`,
    version: 1,
    status: 'PUBLISHED',
    valid_until: '2026-08-31',
    roi_hypotheses: {
      ticket_medio: 220,
      margem_estimada_pct: 45,
      clientes_atuais_mes: 40,
      leads_atuais_mes: 120,
      taxa_conversao_pct: 12,
      investimento_midia_mes: 1000,
    },
    created_by: 'Equipe YouMachine',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });

  return (
    <SectionRenderer
      prospect={prospect}
      proposal={getDynamicProposal()}
      items={[]}
      sections={getSections(prospect.slug)}
    />
  );
};
